import { useState, useEffect, useRef } from "react";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
// LOCAL: reads from /public/platypusbox-data.json out of the box.
// PRODUCTION: replace with your actual GCS URL, e.g.:
//   const GCS_URL = "https://storage.googleapis.com/YOUR_BUCKET/platypusbox-data.json";
const GCS_URL = "/platypusbox-data.json";

// ─── FALLBACK DATA ────────────────────────────────────────────────────────────
const FALLBACK_DATA = {
  company: "PLATYPUSBOX",
  tagline: "LET YOUR IMAGINATION MOVE OUT OF THE BOX",
  tags: ["🎮 Interactive Gaming", "🏟 Events & Exhibitions", "📈 10× Footfall"],
  projects: [
    {
      id: 1,
      title: "AR Catch Zone",
      category: "Augmented Reality",
      description: "A full-body motion-tracking AR game installed at a leading FMCG brand's exhibition stall. Visitors used hand gestures to 'catch' flying branded products on a 98\" display. Average engagement: 4 minutes per visitor.",
      accent: "#FF4D00",
      tags: ["AR", "Motion Sensing", "FMCG"],
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      pdfUrl: "https://www.w3.org/WAI/WCAG21/Techniques/pdf/PDF1.pdf",
      stats: [{ label: "Avg Dwell", value: "4 min" }, { label: "Footfall ↑", value: "8×" }, { label: "Leads", value: "320+" }]
    },
    {
      id: 2,
      title: "Multiplayer Leaderboard Kiosk",
      category: "Competitive Gaming",
      description: "A 4-player live quiz and trivia kiosk synced to a giant LED leaderboard. Deployed for a fintech brand at a Mumbai expo. Real-time score updates kept crowds gathered around the stall for hours.",
      accent: "#00E5FF",
      tags: ["Multiplayer", "Leaderboard", "Fintech"],
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      pdfUrl: "https://www.w3.org/WAI/WCAG21/Techniques/pdf/PDF1.pdf",
      stats: [{ label: "Players/day", value: "600+" }, { label: "Brand Recall", value: "91%" }, { label: "Events", value: "12" }]
    },
    {
      id: 3,
      title: "Photo Booth XR",
      category: "Extended Reality",
      description: "A branded XR photo booth with custom AR filters tied to the client's product launch. Visitors shared to Instagram directly from the booth, generating 1,200+ organic posts in a single weekend.",
      accent: "#FFD600",
      tags: ["XR", "Social Media", "Photo Booth"],
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      pdfUrl: "https://www.w3.org/WAI/WCAG21/Techniques/pdf/PDF1.pdf",
      stats: [{ label: "IG Posts", value: "1.2K" }, { label: "Weekend", value: "1 event" }, { label: "ROI", value: "14×" }]
    },
    {
      id: 4,
      title: "Spin & Win Touchwall",
      category: "Interactive Display",
      description: "A 10-metre interactive touchwall installed at a retail brand's pop-up. Visitors spin branded wheels, unlock offers, and receive coupons on the spot — gamifying the purchase decision.",
      accent: "#B14FFF",
      tags: ["Touchscreen", "Retail", "Gamification"],
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      pdfUrl: "https://www.w3.org/WAI/WCAG21/Techniques/pdf/PDF1.pdf",
      stats: [{ label: "Conversions", value: "38%" }, { label: "Touchpoints", value: "5K/day" }, { label: "Venues", value: "8" }]
    },
    {
      id: 5,
      title: "Esports Stage Setup",
      category: "Live Esports",
      description: "End-to-end esports tournament infrastructure: streaming rig, commentator booth, live scoring overlays, and crowd interaction screens. Delivered for a gaming hardware brand at a national tech expo.",
      accent: "#00FF9C",
      tags: ["Esports", "Live Stream", "Tournament"],
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      pdfUrl: "https://www.w3.org/WAI/WCAG21/Techniques/pdf/PDF1.pdf",
      stats: [{ label: "Live Viewers", value: "22K" }, { label: "Participants", value: "400+" }, { label: "Duration", value: "3 days" }]
    }
  ]
};

function getEmbedUrl(url) {
    if (!url) return null;

    // watch?v=
    if (url.includes("watch?v=")) {
        const id = url.split("watch?v=")[1].split("&")[0];
        return `https://www.youtube.com/embed/${id}`;
    }

    // youtu.be
    if (url.includes("youtu.be/")) {
        const id = url.split("youtu.be/")[1].split("?")[0];
        return `https://www.youtube.com/embed/${id}`;
    }

    // shorts
    if (url.includes("/shorts/")) {
        const id = url.split("/shorts/")[1].split("?")[0];
        return `https://www.youtube.com/embed/${id}`;
    }

    return url; // already embed
}

// ─── PARTICLE CANVAS ──────────────────────────────────────────────────────────
function ParticleCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current, ctx = c.getContext("2d");
    let w = c.width = window.innerWidth, h = c.height = window.innerHeight;
    const pts = Array.from({ length: 70 }, () => ({
      x: Math.random() * w, y: Math.random() * h,
      r: Math.random() * 1.8 + 0.4,
      dx: (Math.random() - 0.5) * 0.35, dy: (Math.random() - 0.5) * 0.35,
      a: Math.random() * 0.45 + 0.08
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      pts.forEach(p => {
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,229,255,${p.a})`; ctx.fill();
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0 || p.x > w) p.dx *= -1;
        if (p.y < 0 || p.y > h) p.dy *= -1;
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    const onResize = () => { w = c.width = window.innerWidth; h = c.height = window.innerHeight; };
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, []);
  return <canvas ref={ref} style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", opacity: 0.3 }} />;
}

// ─── HERO ─────────────────────────────────────────────────────────────────────
function Hero({ data, onStart }) {
  const [vis, setVis] = useState(false);
  useEffect(() => { setTimeout(() => setVis(true), 120); }, []);
  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      position: "relative", textAlign: "center", padding: "40px 24px"
    }}>
      <div style={{
        position: "absolute", width: 600, height: 600, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(255,77,0,0.1) 0%, rgba(0,229,255,0.07) 50%, transparent 70%)",
        filter: "blur(80px)", pointerEvents: "none"
      }} />
      <div style={{
        opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(48px)",
        transition: "all 1.1s cubic-bezier(0.16,1,0.3,1)"
      }}>
              <div style={{
                  width: 72,
                  height: 72,
                  borderRadius: 20,
                  margin: "0 auto 28px",
                  background: "linear-gradient(135deg, #FF4D00, #FFD600)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 0 50px rgba(255,77,0,0.65)",
                  overflow: "hidden" // keeps image inside rounded corners
              }}>
                  <img
                      src="/logo.png"
                      alt="Logo"
                      style={{
                          width: "70%",   // adjust size inside box
                          height: "70%",
                          objectFit: "contain"
                      }}
                  />
              </div>

        <h1 style={{
          fontFamily: "'Bebas Neue', Impact, sans-serif",
          fontSize: "clamp(64px, 14vw, 140px)", letterSpacing: "0.07em",
          background: "linear-gradient(90deg, #FF4D00 0%, #FFD600 35%, #00E5FF 65%, #B14FFF 100%)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          margin: 0, lineHeight: 1
        }}>{data.company}</h1>

        <p style={{
          fontFamily: "'Space Mono', monospace", fontSize: "clamp(12px, 2vw, 18px)",
          color: "rgba(255,255,255,0.55)", letterSpacing: "0.28em", textTransform: "uppercase",
          margin: "18px 0 44px"
        }}>{data.tagline}</p>

        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginBottom: 52 }}>
          {data.tags.map(t => (
            <span key={t} style={{
              fontFamily: "'Space Mono', monospace", fontSize: 11, padding: "7px 18px",
              border: "1px solid rgba(255,255,255,0.18)", borderRadius: 24,
              color: "rgba(255,255,255,0.65)", background: "rgba(255,255,255,0.04)", letterSpacing: "0.05em"
            }}>{t}</span>
          ))}
        </div>

        <button onClick={onStart} style={{
          padding: "17px 46px", borderRadius: 50,
          background: "linear-gradient(135deg, #FF4D00, #FFD600)",
          border: "none", cursor: "pointer", color: "#000",
          fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: "0.1em",
          boxShadow: "0 0 60px rgba(255,77,0,0.5)", transition: "all 0.22s"
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.06)"; e.currentTarget.style.boxShadow = "0 0 90px rgba(255,77,0,0.8)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 0 60px rgba(255,77,0,0.5)"; }}
        >EXPLORE {data.projects.length} PROJECTS →</button>
      </div>

      {/* Ticker */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, overflow: "hidden", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 10, paddingBottom: 10 }}>
        <div style={{
          display: "flex", gap: 40, animation: "ticker 22s linear infinite",
          whiteSpace: "nowrap", color: "rgba(255,255,255,0.2)",
          fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.2em"
        }}>
          {Array(8).fill(["GAMING", "EVENTS", "AR", "EXHIBITIONS", "FOOTFALL", "XR", "BRAND RECALL", "INTERACTIVE"]).flat().map((w, i) => <span key={i}>{w} ◆</span>)}
        </div>
      </div>
    </div>
  );
}

// ─── PROJECT MODAL ────────────────────────────────────────────────────────────
function ProjectModal({ project, onClose }) {
  const [tab, setTab] = useState(project.videoUrl ? "video" : "pdf");
  const [vis, setVis] = useState(false);
  useEffect(() => { setTimeout(() => setVis(true), 30); }, []);

  const close = () => { setVis(false); setTimeout(onClose, 300); };

  useEffect(() => {
    const handler = e => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 500,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "20px",
      background: `rgba(0,0,0,${vis ? 0.85 : 0})`,
      backdropFilter: vis ? "blur(14px)" : "blur(0px)",
      transition: "all 0.3s ease"
    }} onClick={e => e.target === e.currentTarget && close()}>
      <div style={{
        width: "100%", maxWidth: 900, maxHeight: "92vh",
        background: "#0d0d1c", borderRadius: 24,
        border: `1px solid ${project.accent}45`,
        boxShadow: `0 0 100px ${project.accent}25, 0 40px 120px rgba(0,0,0,0.85)`,
        overflow: "hidden", display: "flex", flexDirection: "column",
        opacity: vis ? 1 : 0, transform: vis ? "scale(1) translateY(0)" : "scale(0.93) translateY(24px)",
        transition: "all 0.38s cubic-bezier(0.16,1,0.3,1)"
      }}>

        {/* Accent top bar */}
        <div style={{ height: 3, background: `linear-gradient(90deg, ${project.accent}, ${project.accent}40, transparent)` }} />

        {/* Header */}
        <div style={{
          padding: "22px 28px 18px",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16
        }}>
          <div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "4px 12px", borderRadius: 20, marginBottom: 10,
              background: `${project.accent}18`, border: `1px solid ${project.accent}45`
            }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: project.accent }} />
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: project.accent, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                {project.category}
              </span>
            </div>
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(28px, 4vw, 46px)",
              color: "#fff", margin: 0, letterSpacing: "0.04em"
            }}>{project.title}</h2>
          </div>
          <button onClick={close} style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0, marginTop: 4,
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
            cursor: "pointer", color: "rgba(255,255,255,0.5)", fontSize: 17, lineHeight: 1,
            transition: "all 0.2s"
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
          >✕</button>
        </div>

        {/* Stats */}
        <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          {project.stats.map((s, i) => (
            <div key={i} style={{
              flex: 1, padding: "14px 12px", textAlign: "center",
              borderRight: i < project.stats.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none"
            }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, color: project.accent, letterSpacing: "0.05em" }}>{s.value}</div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(255,255,255,0.35)", letterSpacing: "0.15em", textTransform: "uppercase", marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Description */}
        <div style={{ padding: "18px 28px 6px" }}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14.5, color: "rgba(255,255,255,0.58)", lineHeight: 1.78, margin: 0 }}>
            {project.description}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
            {project.tags.map(t => (
              <span key={t} style={{
                fontFamily: "'Space Mono', monospace", fontSize: 9, padding: "4px 10px", borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em"
              }}>{t}</span>
            ))}
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ padding: "14px 28px 0", display: "flex", alignItems: "center", gap: 8 }}>
          {project.videoUrl && (
            <button onClick={() => setTab("video")} style={{
              padding: "9px 20px", borderRadius: 8, cursor: "pointer",
              background: tab === "video" ? `${project.accent}22` : "rgba(255,255,255,0.04)",
              border: `1px solid ${tab === "video" ? project.accent + "70" : "rgba(255,255,255,0.1)"}`,
              color: tab === "video" ? project.accent : "rgba(255,255,255,0.45)",
              fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.12em",
              transition: "all 0.2s"
            }}>🎬 VIDEO</button>
          )}
          {project.pdfUrl && (
            <button onClick={() => setTab("pdf")} style={{
              padding: "9px 20px", borderRadius: 8, cursor: "pointer",
              background: tab === "pdf" ? `${project.accent}22` : "rgba(255,255,255,0.04)",
              border: `1px solid ${tab === "pdf" ? project.accent + "70" : "rgba(255,255,255,0.1)"}`,
              color: tab === "pdf" ? project.accent : "rgba(255,255,255,0.45)",
              fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.12em",
              transition: "all 0.2s"
            }}>📄 DOCUMENT</button>
          )}
          {project.pdfUrl && (
            <a href={project.pdfUrl} target="_blank" rel="noreferrer" style={{
              marginLeft: "auto", padding: "9px 16px", borderRadius: 8, textDecoration: "none",
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
              color: "rgba(255,255,255,0.4)", fontFamily: "'Space Mono', monospace",
              fontSize: 10, letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: 5
            }}>↗ OPEN</a>
          )}
        </div>

        {/* Media */}
        <div style={{ padding: "14px 28px 28px", flex: 1, overflow: "auto", minHeight: 0 }}>
          {tab === "video" && project.videoUrl && (
                      <div style={{
                          position: "relative",
                          width: "100%",
                          paddingTop: "56.25%", // 16:9 fallback
                          borderRadius: 14,
                          overflow: "hidden",
                          border: `1px solid ${project.accent}30`,
                          boxShadow: `0 0 50px ${project.accent}18`,
                          background: "#000"
                      }}>
                          <iframe
                              src={getEmbedUrl(project.videoUrl)}
                              title={project.title}
                              style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  border: "none"
                              }}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                          />
                      </div>
          )}
          {tab === "pdf" && project.pdfUrl && (
            <div style={{
              borderRadius: 14, overflow: "hidden",
              border: `1px solid ${project.accent}30`,
              boxShadow: `0 0 50px ${project.accent}18`,
              height: 460
            }}>
              <iframe src={project.pdfUrl} title="Document"
                style={{ width: "100%", height: "100%", border: "none" }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── PROJECT CARD ─────────────────────────────────────────────────────────────
function ProjectCard({ project, index, onClick }) {
  const [hover, setHover] = useState(false);
  const [vis, setVis] = useState(false);
  useEffect(() => { setTimeout(() => setVis(true), 80 + index * 80); }, []);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover
          ? "linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.02))"
          : "rgba(255,255,255,0.03)",
        border: `1px solid ${hover ? project.accent + "65" : "rgba(255,255,255,0.07)"}`,
        borderRadius: 20, padding: "26px 26px 22px",
        cursor: "pointer", position: "relative", overflow: "hidden",
        transition: "all 0.35s cubic-bezier(0.16,1,0.3,1)",
        transform: vis ? (hover ? "translateY(-5px)" : "translateY(0)") : "translateY(30px)",
        opacity: vis ? 1 : 0,
        boxShadow: hover ? `0 20px 60px ${project.accent}20` : "none"
      }}>

      {/* BG glow */}
      <div style={{
        position: "absolute", width: 220, height: 220, borderRadius: "50%",
        background: `radial-gradient(circle, ${project.accent}15 0%, transparent 70%)`,
        filter: "blur(40px)", right: -50, top: -50, pointerEvents: "none",
        opacity: hover ? 1 : 0, transition: "opacity 0.4s"
      }} />

      {/* Ghost number */}
      <div style={{
        position: "absolute", right: 14, bottom: 6,
        fontFamily: "'Bebas Neue', sans-serif", fontSize: 96,
        color: `${project.accent}07`, lineHeight: 1, pointerEvents: "none",
        userSelect: "none", letterSpacing: "0.02em"
      }}>{String(index + 1).padStart(2, "0")}</div>

      {/* Category */}
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 16,
        padding: "5px 12px", borderRadius: 20,
        background: `${project.accent}15`, border: `1px solid ${project.accent}40`
      }}>
        <div style={{ width: 5, height: 5, borderRadius: "50%", background: project.accent }} />
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: project.accent, letterSpacing: "0.18em", textTransform: "uppercase" }}>
          {project.category}
        </span>
      </div>

      {/* Title */}
      <h3 style={{
        fontFamily: "'Bebas Neue', sans-serif",
        fontSize: "clamp(22px, 2.8vw, 30px)", letterSpacing: "0.04em",
        color: "#fff", margin: "0 0 12px", lineHeight: 1.1
      }}>{project.title}</h3>

      {/* Description */}
      <p style={{
        fontFamily: "'DM Sans', sans-serif", fontSize: 13,
        color: "rgba(255,255,255,0.48)", lineHeight: 1.72, margin: "0 0 18px",
        display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden"
      }}>{project.description}</p>

      {/* Tags */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 18 }}>
        {project.tags.map(t => (
          <span key={t} style={{
            fontFamily: "'Space Mono', monospace", fontSize: 8, padding: "3px 9px", borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.38)", letterSpacing: "0.1em"
          }}>{t}</span>
        ))}
      </div>

      {/* Stats row */}
      <div style={{
        display: "flex",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        marginBottom: 18
      }}>
        {project.stats.map((s, i) => (
          <div key={i} style={{
            flex: 1, padding: "10px 6px", textAlign: "center",
            borderRight: i < project.stats.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none"
          }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: project.accent, letterSpacing: "0.04em" }}>{s.value}</div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7.5, color: "rgba(255,255,255,0.32)", letterSpacing: "0.12em", textTransform: "uppercase", marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Footer row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {project.videoUrl && (
            <span style={{
              fontFamily: "'Space Mono', monospace", fontSize: 8, padding: "3px 9px", borderRadius: 10,
              background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.38)", letterSpacing: "0.08em"
            }}>🎬 VIDEO</span>
          )}
          {project.pdfUrl && (
            <span style={{
              fontFamily: "'Space Mono', monospace", fontSize: 8, padding: "3px 9px", borderRadius: 10,
              background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.38)", letterSpacing: "0.08em"
            }}>📄 DOC</span>
          )}
        </div>
        <div style={{
          fontFamily: "'Space Mono', monospace", fontSize: 9,
          color: hover ? project.accent : "rgba(255,255,255,0.28)",
          letterSpacing: "0.12em", transition: "color 0.25s",
          display: "flex", alignItems: "center", gap: 5
        }}>
          VIEW FULL
          <span style={{ display: "inline-block", transform: hover ? "translateX(4px)" : "translateX(0)", transition: "transform 0.25s" }}>→</span>
        </div>
      </div>
    </div>
  );
}

// ─── PROJECTS PAGE ────────────────────────────────────────────────────────────
function ProjectsPage({ data, onBack }) {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [headerVis, setHeaderVis] = useState(false);
  useEffect(() => { setTimeout(() => setHeaderVis(true), 80); }, []);

  const categories = ["ALL", ...Array.from(new Set(data.projects.map(p => p.category)))];
  const filtered = filter === "ALL" ? data.projects : data.projects.filter(p => p.category === filter);

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Nav */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "13px 28px", flexWrap: "wrap", gap: 10,
        background: "rgba(6,6,14,0.9)", backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)"
      }}>
        <button onClick={onBack} style={{
          display: "flex", alignItems: "center", gap: 10,
          background: "none", border: "none", cursor: "pointer", padding: 0
        }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <img
                          src="/logo.png"
                          alt="PLATYPUSBOX Logo"
                          style={{ height: 36 }}
                      />
                  </div>
          <span style={{
            fontFamily: "'Bebas Neue', sans-serif", fontSize: 19,
            background: "linear-gradient(90deg, #FF4D00, #FFD600)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            letterSpacing: "0.1em"
          }}>PLATYPUSBOX</span>
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilter(cat)} style={{
              padding: "6px 13px", borderRadius: 7, cursor: "pointer",
              fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.1em",
              background: filter === cat ? "rgba(255,77,0,0.18)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${filter === cat ? "#FF4D0055" : "rgba(255,255,255,0.09)"}`,
              color: filter === cat ? "#FF6B30" : "rgba(255,255,255,0.4)",
              transition: "all 0.2s"
            }}>{cat}</button>
          ))}
        </div>
      </nav>

      {/* Page header */}
      <div style={{
        padding: "44px 32px 20px", maxWidth: 1200, margin: "0 auto", width: "100%",
        opacity: headerVis ? 1 : 0, transform: headerVis ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.7s cubic-bezier(0.16,1,0.3,1)"
      }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <p style={{
              fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#FF4D00",
              letterSpacing: "0.28em", textTransform: "uppercase", marginBottom: 10
            }}>OUR SOLUTIONS FOR YOU</p>
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(38px, 6vw, 70px)", letterSpacing: "0.04em",
              color: "#fff", margin: 0, lineHeight: 1
            }}>PROJECT SHOWCASE</h2>
          </div>
          <p style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: 13.5,
            color: "rgba(255,255,255,0.38)", maxWidth: 380, lineHeight: 1.75, margin: 0
          }}>
            Each installation is purpose-built for your brand. Click any project to watch the video and explore the full case document.
          </p>
        </div>
        <div style={{ marginTop: 22, height: 2, borderRadius: 1, background: "linear-gradient(90deg, #FF4D00, #FFD600 40%, transparent)" }} />
      </div>

      {/* Grid */}
      <div style={{
        flex: 1, padding: "12px 32px 64px",
        maxWidth: 1200, margin: "0 auto", width: "100%",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))",
        gap: 18, alignContent: "start"
      }}>
        {filtered.map((p, i) => (
          <ProjectCard key={p.id} project={p} index={i} onClick={() => setSelected(p)} />
        ))}
      </div>

      {/* Footer */}
      <div style={{
        borderTop: "1px solid rgba(255,255,255,0.05)", padding: "18px 32px",
        textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 9,
        color: "rgba(255,255,255,0.18)", letterSpacing: "0.18em"
      }}>
        © PLATYPUSBOX · contact@platypusbox.com · LET YOUR IMAGINATION MOVE OUT OF THE BOX
      </div>

      {selected && <ProjectModal project={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ─── LOADING ──────────────────────────────────────────────────────────────────
function Loading() {
  const [dots, setDots] = useState("");
  useEffect(() => {
    const t = setInterval(() => setDots(d => d.length >= 3 ? "" : d + "."), 380);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 20 }}>
      <div style={{
        width: 64, height: 64, borderRadius: 16,
        background: "linear-gradient(135deg, #FF4D00, #FFD600)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 32, animation: "pulse 1.6s ease-in-out infinite"
      }}>🦆</div>
      <p style={{ fontFamily: "'Space Mono', monospace", color: "rgba(255,255,255,0.35)", letterSpacing: "0.22em", fontSize: 11 }}>LOADING{dots}</p>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [data, setData] = useState(null);
  const [stage, setStage] = useState("hero");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(GCS_URL)
      .then(r => r.json())
      .then(json => { setData(json); setLoading(false); })
      .catch(() => { setData(FALLBACK_DATA); setLoading(false); });
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Space+Mono:wght@400;700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { background: #06060e; color: #fff; overflow-x: hidden; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #0a0a18; }
        ::-webkit-scrollbar-thumb { background: #FF4D00; border-radius: 2px; }
        @keyframes ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 30px rgba(255,77,0,0.4); }
          50% { transform: scale(1.1); box-shadow: 0 0 70px rgba(255,77,0,0.85); }
        }
      `}</style>
      <ParticleCanvas />
      <div style={{ position: "relative", zIndex: 1 }}>
        {loading
          ? <Loading />
          : stage === "hero"
            ? <Hero data={data} onStart={() => setStage("projects")} />
            : <ProjectsPage data={data} onBack={() => setStage("hero")} />
        }
      </div>
    </>
  );
}
